//---- Dependencies ----//
import "./axiosInterceptors";
import React, { Suspense } from 'react';
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { Router, Route, browserHistory, IndexRedirect } from "react-router";
import { I18nextProvider } from "react-i18next";
import { SnackbarProvider } from "notistack";
import ReactGA from 'react-ga4';
import CssBaseline from '@mui/material/CssBaseline';
import * as Sentry from "@sentry/react";
import axios from "axios";
import { withSuspense } from "./Components/Common/hoc/withSuspense";
import { browserTracingIntegration } from "@sentry/browser";

import i18n from "./i18n";
import { store } from "./Store";

//---- Actions ----//
import { SetLocales, SetUserAndConnection, SetUserPermissions } from "./Actions/User";

//---- Commons ----//
import { detectEnv } from "./Components/Common/utils/detectEnv";
import { getActiveFunnels } from "./getActiveFunnels";
import CloseSnackbar from "./Components/Common/CloseSnackbar";
import GetCookie from "./Components/Common/Functions/GetCookie";

//---- CSS ----//
import "./Icons/styles.css";
import "./NewIcons/styles.css";
import "./Style/Global.css";
import 'react-virtualized/styles.css';
import "./Components/Menu/MaterialTripList/MailVisualEditor/css/index.css";

//---- Components ----//
import { withCompatTheme } from "./Components/Menu/Settings/hoc/withCompatTheme";
import SetCookie from "./Components/Common/Functions/SetCookie";
import { ThemeProvider, StyledEngineProvider } from "./Components/Menu/Settings/theme/themeProvider";
import { ToggleAccommodationRecommended } from "./Actions/Accommodation";
import { SetCurrencyList } from "./Actions/Base";
import CheckBeforeRequest from "./Components/Common/CheckBeforeRequest";
import Notify from "./Components/Common/Notify";

import { resetCircuit, setFunnelId } from "./Components/pages/redux/circuitSlice.ts";
import { setSeeProductsMargins } from "./Components/CartMaterial/redux/cartConstructionReducer";

//---- NotFound ----//
const NotFound = withSuspense(
    React.lazy(() =>
        import("./Components/NotFound/NotFound.jsx")
    )
);

//---- Base -> Header - Component ----//
const Base = withSuspense(
    React.lazy(() =>
        import("./Components/Base/Base.jsx")
    )
);

//---- Itinerary ----//
const Itinerary = withSuspense(
    React.lazy(() =>
        import("./Components/Itinerary/Itinerary.tsx")
    )
);

//---- PassengerGroup ----//
const FlightGroups = withSuspense(
    React.lazy(() =>
        import("./Components/FlightMaterial/FlightGroups/FlightGroups.js")
    )
);

//---- FlightSearch ----//
const FlightSearch = withSuspense(
    React.lazy(() =>
        import("./Components/FlightMaterial/FlightSearch/FlightSearch.js")
    )
);

//---- SelectedFlight ----//
const SelectedFlight = withSuspense(
    React.lazy(() =>
        import("./Components/FlightMaterial/FlightSelected/FlightSelected.js")
    )
);

//---- Accommodation ----//
const AccommodationList = withSuspense(
    React.lazy(() =>
        import("./Components/Accommodation/AccommodationList")
    )
);
const MaterialAccommodationCard = withSuspense(
    React.lazy(() =>
        import("./Components/Accommodation/MaterialAccommodationCard.jsx")
    )
);

//---- Poi ----//
const PoiList = withSuspense(
    React.lazy(() =>
        import("./Components/PoiMaterial/PoiList.js")
    )
);
const PoiCard = withSuspense(
    React.lazy(() =>
        import("./Components/PoiMaterial/PoiCard.js")
    )
);

//---- Cars ----//
const CarsSearch = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsSearch.jsx")
    )
);
//---- MaterialCars ----//
const MaterialCarsSearch = withSuspense(
    React.lazy(() =>
        import("./Components/CarsMaterial/MaterialCarsSearch.js")
    )
);
const CarsSearchOneStep = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsSearchOneStep.jsx")
    )
);
const CarsStep = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsStep.jsx")
    )
);
const CarsResults = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsResults.jsx")
    )
);
const CarsAddToCart = withSuspense(
    React.lazy(() =>
        import("./Components/Cars/CarsSearch/CarsAddToCart.jsx")
    )
);

//---- Cart ----//
const Cart = withSuspense(
    React.lazy(() =>
        import("./Components/CartMaterial/Cart.js")
    )
);
const CartQuotation = withSuspense(
    React.lazy(() => {
        import("./Components/CartMaterial/CartQuotation.js");
    })
);
//---- Booking ----//
const Booking = withSuspense(
    React.lazy(() =>
        import("./Components/BookingMaterial/Booking.js")
    )
);
//---- Summary-Quotation ----//
const SummaryQuotation = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryQuotation.js")
    )
);
const SummaryPayment = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryPayment.js")
    )
);
const SummaryPaymentSuccess = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryPaymentSuccess.js")
    )
);
const SummaryPaymentError = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryPaymentError.js")
    )
);
const SummaryDocuments = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryDocuments.js")
    )
);
const SummaryInscription = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryInscription.js")
    )
);
const SummaryContact = withSuspense(
    React.lazy(() =>
        import("./Components/MaterialSummaryQuotation/SummaryContact.js")
    )
);

//---- Provider-Website ----//
const ProviderWebsite = withSuspense(
    React.lazy(() =>
        import("./Components/ProviderWebsite/MaterialProviderWebsite.js")
    )
);
const ProviderQuotation = withSuspense(
    React.lazy(() =>
        import("./Components/ProviderQuotation/ProviderQuotation.tsx")
    )
);
const ProviderBooking = withSuspense(
    React.lazy(() =>
        import("./Components/ProviderBooking/ProviderBooking.tsx")
    )
);
//---- PCancel-Website ----//
const ProviderCancel = withSuspense(
    React.lazy(() =>
        import("./Components/ProviderWebsite/ProviderCancel.jsx")
    )
);

//---- Home ----//
const Home = withSuspense(
    React.lazy(() =>
        import("./Components/pages/home.tsx")
    )
);
const Login = withSuspense(
    React.lazy(() =>
        import("./Components/Home/MaterialLoginPage")
    )
);
//---- ItineraryType & others products ----//
const DestinationProducts = withSuspense(
    React.lazy(() =>
        import("./Components/pages/destination.tsx")
    )
);
const NoDestinationProducts = withSuspense(
    React.lazy(() =>
        import("./Components/pages/noDestination")
    )
);
//---- ItineraryTypeDetails & others products ----//
const ItineraryTypeDetails = withSuspense(
    React.lazy(() =>
        import("./Components/pages/itineraryType.tsx")
    )
);
const PackagesDetails = withSuspense(
    React.lazy(() =>
        import("./Components/ItineraryType/PackagesMaterial/PackagesDetails.js")
    )
);
const CircuitsDetails = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuit.tsx")
    )
);

//---- Menu ----//
//---- BaseMenu ----//
const BaseMenu = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/BaseMenu/MaterialBaseMenu.js")
    )
);
//---- Profile ----//
const Profile = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Profile/MaterialProfile.js")
    )
);

//---- Agency Profile ----//
const AgencyProfile = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/AgencyProfile/MaterialAgencyProfile.js")
    )
);

const SalesPersons = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/SalesPersons/salesPersons")
    )
);

const ThemeSettings = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/theme/themeSettings.tsx")
    )
);

const ThemeSettingsColors = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/theme/themeSettingsColors.tsx")
    )
);

const ThemeSettingsInputs = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/theme/themeSettingsInputs.tsx")
    )
);

const ThemeSettingsTypography = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/theme/themeSettingsTypography.tsx")
    )
);

const ThemeSettingsButtons = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/theme/themeSettingsButtons.tsx")
    )
);

const ThemeSettingsIcons = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/theme/themeSettingsIcons.tsx")
    )
);

const TripList = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/MaterialTripList/TripList")
    )
);

const TripListInfo = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/MaterialTripList/TripListInfo")
    )
);

//---- AgencyMarging ----//
const AgencyMargin = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/AgencyMargin/MarginList.js")
    )
);

//---- AgencyUserManagement ----//
const AgencyUserManagement = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/AgencyUserManagement/AgencyUserTable.js")
    )
);

//---- AgencyCustomerManagement ----//
const AgencyCustomerManagement = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/AgencyCustomerManagement/AgencyCustomerManagement.jsx")
    )
);

//---- Help ----//
const Help = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Help/TripListVideo.jsx")
    )
);

//---- Settings ----//
const Settings = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/Settings.jsx")
    )
);

//---- Transfers ----//
const Transfers = withSuspense(
    React.lazy(() =>
        import("./Components/Transfers/Transfers.tsx")
    )
);
const StepTransfers = withSuspense(
    React.lazy(() =>
        import("./Components/Transfers/StepTransfers.jsx")
    )
);
const CityTransfers = withSuspense(
    React.lazy(() =>
        import("./Components/Transfers/CityTransfers.jsx")
    )
);

const AgencyOfflineMargin = withSuspense(
    React.lazy(() =>
        import("./Components/AgencyOfflineMargin/AgencyOfflineMargin")
    )
);

const CmsSettings = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/CmsSettings.tsx")
    )
);

const ThemeSettingsList = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/theme/themeSettingsList.tsx")
    )
);

const CircuitSteps = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitSteps.tsx")
    )
);

const CircuitStepsParameters = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitStepsTripParameters.tsx")
    )
);

const CircuitStepsAccommodation = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitStepsAccommodation.tsx")
    )
);

const CircuitStepsProducts = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitStepsProducts.tsx")
    )
);

const CircuitStepsOptionalProducts = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitStepsOptionalProducts.tsx")
    )
);

const CircuitStepsInsurances = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitStepsInsurances.tsx")
    )
);

const CircuitBookingTravelers = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitBookingTravelers.tsx")
    )
);

const CircuitStepsBookingCartRecap = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitStepsBookingCartRecap.tsx")
    )
);

const CircuitCartSteps = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartSteps.tsx")
    )
);

const CircuitCartStepsBookingCartRecap = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsBookingCartRecap.tsx")
    )
);

const CircuitCartStepsTravelers = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsTravelers.tsx")
    )
);

const CircuitCartStepsBooking = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsBooking.tsx")
    )
);

const CircuitCartStepsAccommodation = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsAccommodation.tsx")
    )
);

const CircuitCartStepsProducts = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsProducts.tsx")
    )
);

const CircuitCartStepsOptionalProducts = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsOptionalProducts.tsx")
    )
);

const CircuitCartStepsFlights = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsFlights.tsx")
    )
);

const CircuitCartStepsInsurances = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitCartStepsInsurances.tsx")
    )
);

const Destinations = withSuspense(
    React.lazy(() =>
        import("./Components/Home/MaterialHome")
    )
);

const Booklets = withSuspense(
    React.lazy(() =>
        import("./Components/pages/booklets")
    )
);

const OurContacts = withSuspense(
    React.lazy(() =>
        import("./Components/pages/ourContacts")
    )
);

const LegalMentions = withSuspense(
    React.lazy(() =>
        import("./Components/pages/legalMentions")
    )
);

const Us = withSuspense(
    React.lazy(() =>
        import("./Components/pages/us")
    )
);

const Insurance = withSuspense(
    React.lazy(() =>
        import("./Components/pages/insurance")
    )
);

const Cgv = withSuspense(
    React.lazy(() =>
        import("./Components/pages/cgv")
    )
);

const StarsOfRoyalPalm = withSuspense(
    React.lazy(() =>
        import("./Components/pages/starsOfRoyalPalm")
    )
);

const BeachcomberAdventure = withSuspense(
    React.lazy(() =>
        import("./Components/pages/beachcomberAdventure")
    )
);

const Ponant = withSuspense(
    React.lazy(() =>
        import("./Components/pages/ponant")
    )
);

const AgentsVacations = withSuspense(
    React.lazy(() =>
        import("./Components/pages/agentsVacations")
    )
);

const Insurances = withSuspense(
    React.lazy(() =>
        import("./Components/CartMaterial/insurances")
    )
);

const Flyers = withSuspense(
    React.lazy(() =>
        import("./Components/pages/flyers")
    )
);

const AirFrance = withSuspense(
    React.lazy(() =>
        import("./Components/pages/airFrance")
    )
);

const ShandraniBeachcomber = withSuspense(
    React.lazy(() =>
        import("./Components/pages/shandraniBeachcomber")
    )
);

const EndOfYearCelebrations = withSuspense(
    React.lazy(() =>
        import("./Components/pages/endOfYearCelebrations")
    )
);

const Travelers = withSuspense(
    React.lazy(() =>
        import("./Components/CartMaterial/travelers")
    )
);

const GlobalUtils = withSuspense(
    React.lazy(() =>
        import("./Components/Common/globalUtils")
    )
);

const CircuitB2bParameters = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bTripParameters.tsx")
    )
);

const CircuitB2bSteps = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bSteps.tsx")
    )
);

const CircuitB2bAccommodationBasedOnSource = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bAccommodationBasedOnSource")
    )
);

const CircuitB2bPois = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bPois")
    )
);

const CircuitB2bTransfers = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bTransfers")
    )
);

const CircuitB2bCars = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bCars.tsx")
    )
);

const CircuitB2bProducts = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bProducts.tsx")
    )
);

const CircuitB2bOptionalProducts = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bOptionalProducts.tsx")
    )
);

const CircuitB2bInsurances = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bInsurances.tsx")
    )
);

const CircuitB2bBookingCartRecap = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2bBookingCartRecap.tsx")
    )
);

const BoRedirection = withSuspense(
    React.lazy(() =>
        import("./Components/Common/boRedirection")
    )
);

const TripListDocumentPage = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/MaterialTripList/TripListDocumentPage")
    )
);

const Sso = withSuspense(
    React.lazy(() =>
        import("./Components/Home/sso")
    )
);

const DeactivateWebsite = withSuspense(
    React.lazy(() =>
        import("./Components/Home/deactivateWebsite")
    )
);

const MaterialLoginPage = withSuspense(
    React.lazy(() =>
        import("./Components/Home/MaterialLoginPage")
    )
);

const CircuitB2bBookingEmailConfirmation = withSuspense(
    React.lazy(() =>
        import("./Components/pages/circuitB2cBookingEmailConfirmation.tsx")
    )
);


const PageBuilder = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/page-builder/pageBuilder")
    )
);

const PageBuilderViewPage = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/page-builder/pageBuilderViewPage")
    )
);

const PageBuilderPreview = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/page-builder/pageBuilderPreview")
    )
);

const PageBuilderTemplatePreview = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/page-builder/pageBuilderTemplatePreview")
    )
);

const PagesList = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/pages/pagesList")
    )
);

const MenusList = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/pages/menusList")
    )
);

const MenuEdit = withSuspense(
    React.lazy(() =>
        import("./Components/Menu/Settings/pages/menuEdit")
    )
);

const requireAuth = (router, replace, callback) => {
    let token = GetCookie("token");
    const config = JSON.parse(localStorage.getItem('config') ?? '{}');
    const cmsConfig = config.cms_list?.find((item) => {
        const domainName = window.location.hostname.startsWith("www.") ?
            window.location.hostname.replace("www.", "") :
            window.location.hostname;
        return item.domain === domainName;
    });
    if (
        !token &&
        (
            !cmsConfig ||
            cmsConfig.authentication_required
        )
    ) {
        replace({
            pathname: '/login',
            state: { nextPathname: router.location.pathname }
        });
        callback();
    }
    callback();
};

const checkDepartureReturn = (router, replace, callback, store) => {
    if (store.getState().itinerary.departure_destination.city === null && store.getState().itinerary.return_destination.city === null) {
        replace({
            pathname: `/${router.params.url_name}/apps/itinerary`,
            state: { nextPathname: router.location.pathname }
        });
    }
    callback();
};

const checkFlight = (router, replace, callback, store) => {
    if (store.getState().flight.index_group === null || store.getState().flight.copy_index_group === null) {
        replace({
            pathname: `/${router.params.url_name}/apps/flight-groups`,
            state: { nextPathname: router.location.pathname }
        });
    }
    callback();
};

const getInstance = (url_name, store) => {
    let domain_name = detectEnv().domainName;
    let customer_identifier = detectEnv().identifier;
    return axios.get(
        `${API_HREF}instances/?customer_identifier=${customer_identifier}&domain_name=${domain_name}`
    ).then((response) => {
        const data = response.data;
        if (detectEnv().mirror === 'bonogo') {
            let link = document.querySelector("link[rel*='icon']") || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = "/Img/cms/bonogo-favicon.png";
            document.getElementsByTagName('head')[0].appendChild(link);
        } else if (data.favicon !== undefined && data.favicon !== null && data.favicon.thumbnail_little !== undefined && data.favicon.url !== undefined) {
            let link = document.querySelector("link[rel*='icon']") || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = (data.favicon.thumbnail_little !== null ? data.favicon.thumbnail_little : data.favicon.url);
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        // if (data.analytics !== null) {
        //     ReactGA.initialize(data.analytics);
        //     ReactGA.pageview(window.location.pathname + window.location.search);
        // }
        localStorage.setItem("config", JSON.stringify(data));
        if (data.quotation_code === "amerigo" && !store.getState().accommodation.recommended) {
            store.dispatch(ToggleAccommodationRecommended());
        }
        window.id_owner = data.instance_id;
        window.id_buyer = data.client_id;
        window.url_name = customer_identifier;
        return response;
    }).catch((error) => {
        console.log(error);
        if (error !== undefined && error !== null && error.status !== undefined && error.status !== null && error.status === 404) {
            Notify(/*i18n.t("notify.wrong_instance")*/ "L'instance que vous essayez d'accéder n'existe pas, veuillez vérifier votre url d'accès", { status: "danger", pos: "top-center" });
        }
    });
};

const getLocales = (store) => {
    const { pass_check, headers } = CheckBeforeRequest();
    return axios.get(`${API_HREF}locales/`).then((response) => {
        const data = response.data;
        const default_locale = data.results.find((el) => {
            return el.language_code === 'fr';
        });
        const main_locale = data.results.find((el) => {
            return el.language_code === i18n.language;
        });
        store.dispatch(SetLocales(data.results));
        store.dispatch({ type: "USER_SET_MAIN_LOCALE", payload: main_locale ? main_locale : default_locale });
    }).catch((error) => {
        console.log(error);
    });
};

const getCurrencies = (store) => {
    const { pass_check, headers } = CheckBeforeRequest();
    if (store.getState().base.currency_list.length === 0) {
        axios.get(`${API_HREF}currencies/?limit=200`).then((response) => {
            const data = response.data;
            if (data.results.length > 0) {
                store.dispatch(SetCurrencyList(data.results));
            }
        }).catch((error) => {
            console.log(error);
        });
    }
};

const loadProfile = (store, router) => {
    let token = GetCookie("token");
    let id_user = GetCookie("id_user");
    let client_user = GetCookie("client_user");
    let domain_name = window.location.host.replace("www.", "");
    if (token && store.getState().user.user === null) {
        return axios.get(
            `${API_HREF}client/${client_user}/user/${id_user}/`,
            {
                headers: {
                    "Authorization": "Token " + token,
                    "Accept": "application/json",
                    "Content-Type": "application/json"
                }
            }
        ).then((response) => {
            const data = response.data;
            if (JSON.parse(localStorage.getItem("config") ?? '{}').quotation_code === "amerigo" && !data.is_superuser) {
                Notify("FacilitaTrip est forcée d'interrompre le service Free Independent Traveler pour le compte du tour-opérateur Amerigo. Nous en sommes sincèrement désolés, mais nous poursuivons l'aventure avec d'autres partenaires", { status: "danger", pos: "top-center" });
            } else if (JSON.parse(localStorage.getItem("config") ?? '{}').quotation_code === "pbe" && !data.is_superuser) {
                Notify("FacilitaTrip est forcée d'interrompre le service Free Independent Traveler pour le compte du tour-opérateur Plus Belle l'Europe. Nous en sommes sincèrement désolés, mais nous poursuivons l'aventure avec d'autres partenaires", { status: "danger", pos: "top-center" });
            } else {
                store.dispatch(SetUserAndConnection(data, true));
                if (!!data.lang && data.lang.split("-").length > 0) {
                    store.dispatch({ type: 'CART_SET_QUOTATION_TARGET_LANGUAGE', payload: data.lang.split("-")[0] })
                }
                if (data.is_superuser === false) {
                    axios.get(
                        `${API_HREF}client/${client_user}/user/${id_user}/permissions/?all=true`,
                        {
                            headers: {
                                "Authorization": "Token " + token,
                                "Accept": "application/json",
                                "Content-Type": "application/json"
                            }
                        }
                    ).then((response) => {
                        const data_perm = response.data;
                        store.dispatch(SetUserPermissions(data_perm));
                    }).catch((error) => {
                        console.log(error);
                    });
                } else {
                    axios.get(
                        `${API_HREF}permissions/?all=true`,
                        {
                            headers: {
                                "Authorization": "Token " + token,
                                "Accept": "application/json",
                                "Content-Type": "application/json"
                            }
                        }
                    ).then((response) => {
                        const data_perm = response.data;
                        store.dispatch(SetUserPermissions(data_perm));
                    }).catch((error) => {
                        console.log(error);
                    });
                }
            }
        }).catch((error) => {
            console.log(error);
        });
    }
};

const loadTrip = (router) => {
    let user_token = GetCookie("token");
    let id_user = GetCookie("id_user");
    let client_user = GetCookie("client_user");
    //Delete the cookie
    let cookies = document.cookie.split("; ");
    for (let c = 0; c < cookies.length; c++) {
        let d = window.location.hostname.split(".");
        while (d.length > 0) {
            let cookieBase = encodeURIComponent(cookies[c].split(";")[0].split("=")[0]) + "=; expires=Thu, 01-Jan-1970 00:00:01 GMT; domain=" + d.join(".") + " ;path=";
            let p = location.pathname.split("/");
            document.cookie = cookieBase + "/";
            while (p.length > 0) {
                document.cookie = cookieBase + p.join("/");
                p.pop();
            }
            d.shift();
        }
    }
    SetCookie("trip_id", router.params.trip_id, 365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
    SetCookie("trip_id_version", router.params.trip_version, 365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
    SetCookie("trip_token", router.params.trip_token, 365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
    SetCookie("token", user_token, 365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
    SetCookie("id_user", id_user, 365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
    SetCookie("client_user", client_user, 365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));

    if (router.location.query.redirect === 'cart') {
        window.location = `/${router.params.url_name}/apps/cart`;
    } else {
        window.location = `/${router.params.url_name}/apps/itinerary`;
    }
};

if (['production', 'staging'].includes(detectEnv().env)) {
    Sentry.init({
        dsn: "https://3354227df6b63bb4c91a4f3507060285@o161232.ingest.us.sentry.io/4507175858929664",
        integrations: [browserTracingIntegration()],
        //We recommend adjusting this value in production, or using tracesSampler
        //for finer control
        tracesSampleRate: 1.0
    });
}

if (window.location.hostname.includes("beachpro.fr")) {
    ReactGA.initialize("G-P3W9L02KCZ");
}

function RoutesWrapper(props) {
    return (
        <Provider store={store}>
            <I18nextProvider i18n={i18n}>
                <Suspense fallback="loading">
                    <ThemeProvider>
                        <CssBaseline>
                            <SnackbarProvider anchorOrigin={{ vertical: "top", horizontal: "right" }} disableWindowBlurListener={true} action={key => CloseSnackbar(key)}>
                                {props.children}
                            </SnackbarProvider>
                        </CssBaseline>
                    </ThemeProvider>
                </Suspense>
            </I18nextProvider>
        </Provider>
    );
}

ReactDOM.render(
    <span>Loading...</span>,
    document.getElementById("app")
);

const [response] = await Promise.all([
    getInstance(),
    loadProfile(store),
    getLocales(store)
]);
const cms = response?.data.cms_list?.find((item) => {
    const domainName = window.location.hostname.startsWith("www.") ?
        window.location.hostname.replace("www.", "") :
        window.location.hostname;
    return item.domain === domainName;
});

if (cms?.is_active === false) {
    ReactDOM.render(
        <RoutesWrapper>
            <DeactivateWebsite />
        </RoutesWrapper>,
        document.getElementById("app")
    );
} else {
    const funnels = cms ?
        await getActiveFunnels(cms.id) :
        [];
    const tailorMadeFunnel = funnels.find((item) => {
        return item.type === 'TAILOR_MADE';
    });
    const packageFunnel = funnels.find((item) => {
        return item.type === 'PROCESS_PACKAGE';
    });
    ReactDOM.render(
        <RoutesWrapper>
            <Router history={browserHistory}>
                <Route component={GlobalUtils}>
                    <Route path="/" component={Home} onEnter={(router) => {
                        getCurrencies(store);
                    }} />
                    <Route path="/login" component={Login} onEnter={(router) => {
                        getCurrencies(store);
                    }} />
                    <Route
                        path={"email-confirmation"}
                        component={CircuitB2bBookingEmailConfirmation}
                        onEnter={(router) => {
                            getCurrencies(store);
                        }}
                    />
                    <Route
                        path={"email-confirmation"}
                        component={CircuitB2bBookingEmailConfirmation}
                        onEnter={(router) => {
                            getCurrencies(store);
                            getLocales(store);
                        }}
                    />
                    <Route
                        path={"email-confirmation"}
                        component={CircuitB2bBookingEmailConfirmation}
                        onEnter={(router) => {
                            getCurrencies(store);
                            getLocales(store);
                        }}
                    />
                    <Route path={"access/:url_name/:trip_id/:trip_version/:trip_token"} onEnter={(router, replace, callback) => {
                        requireAuth(router, replace, callback);
                        loadTrip(router);
                    }} />
                    <Route path={"agency-price-edit/:trip_id/:trip_version/:trip_token/:token"} component={AgencyOfflineMargin} onEnter={(router) => {
                        getCurrencies(store);
                    }} />
                    <Route path={"sso"} component={Sso} />
                    <Route
                        path={"bo/redirect"}
                        component={BoRedirection}
                        onEnter={(router) => {
                            const PARAMS_LIST = [
                                'client_user',
                                'id_user',
                                'token',
                                'trip_id',
                                'trip_id_version',
                                'trip_token'
                            ];
                            for (const key of PARAMS_LIST) {
                                if (router.location.query[key]) {
                                    SetCookie(
                                        key,
                                        router.location.query[key],
                                        365,
                                        `/`,
                                        window.location.host === "localhost:3000" ?
                                            null :
                                            window.location.host
                                    );
                                }
                            }
                        }}
                    />
                    <Route path={"/:url_name"} onEnter={(router) => {
                        getCurrencies(store);
                        if (tailorMadeFunnel) {
                            store.dispatch(
                                setFunnelId(tailorMadeFunnel.id)
                            );
                        }
                    }}>
                        <Route
                            path="destinations"
                            component={Destinations}
                            onEnter={() => {
                                if (packageFunnel) {
                                    store.dispatch(
                                        setFunnelId(packageFunnel.id)
                                    );
                                }
                            }}
                            onLeave={() => {
                                if (tailorMadeFunnel) {
                                    store.dispatch(
                                        setFunnelId(tailorMadeFunnel.id)
                                    );
                                }
                            }}
                        />
                        <Route path={"reset-password"} component={Login} />
                        <Route
                            path={"destination-product/:desti_id"}
                            component={DestinationProducts}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                                if (packageFunnel) {
                                    store.dispatch(
                                        setFunnelId(packageFunnel.id)
                                    );
                                }
                            }}
                            onLeave={() => {
                                if (tailorMadeFunnel) {
                                    store.dispatch(
                                        setFunnelId(tailorMadeFunnel.id)
                                    );
                                }
                            }}
                        />
                        <Route
                            path={"no-destination"}
                            component={NoDestinationProducts}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                                if (packageFunnel) {
                                    store.dispatch(
                                        setFunnelId(packageFunnel.id)
                                    );
                                }
                            }}
                            onLeave={() => {
                                if (tailorMadeFunnel) {
                                    store.dispatch(
                                        setFunnelId(tailorMadeFunnel.id)
                                    );
                                }
                            }}
                        />
                        <Route path={"booklets"} component={Booklets} />
                        <Route path={"our-contacts"} component={OurContacts} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"legal-mentions"} component={LegalMentions} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"us"} component={Us} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"insurance"} component={Insurance} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"cgv"} component={Cgv} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"stars-of-royal-palm"} component={StarsOfRoyalPalm} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"beachcomber-adventure"} component={BeachcomberAdventure} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"ponant"} component={Ponant} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"agents-vacations"} component={AgentsVacations} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"flyers"} component={Flyers} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"air-france"} component={AirFrance} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"shandrani-beachcomber"} component={ShandraniBeachcomber} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route path={"end-of-year-celebrations"} component={EndOfYearCelebrations} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} />
                        <Route
                            path={"packages-details/:itinerary_type_name/:itinerary_type_id"}
                            component={PackagesDetails}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                                if (packageFunnel) {
                                    store.dispatch(
                                        setFunnelId(packageFunnel.id)
                                    );
                                }
                            }}
                            onLeave={() => {
                                if (tailorMadeFunnel) {
                                    store.dispatch(
                                        setFunnelId(tailorMadeFunnel.id)
                                    );
                                }
                            }}
                        />
                        <Route
                            path={"circuits-details/:itinerary_type_name/:itinerary_type_id(/:version)"}
                            component={CircuitsDetails}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                                if (packageFunnel) {
                                    store.dispatch(
                                        setFunnelId(packageFunnel.id)
                                    );
                                }
                            }}
                            onLeave={() => {
                                if (tailorMadeFunnel) {
                                    store.dispatch(
                                        setFunnelId(tailorMadeFunnel.id)
                                    );
                                }
                            }}
                        />
                        <Route
                            path={"itinerary-type-details/:itinerary_type_name/:itinerary_type_id"}
                            component={ItineraryTypeDetails}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                            }}
                        />
                        <Route path={"summary-quotation/payment/:trip_id/:trip_id_version/:trip_token"} component={SummaryPayment} show_map_calendar={false} />
                        <Route path={"summary-quotation/:trip_id/:trip_id_version/:trip_token"} component={SummaryQuotation} show_map_calendar={false} />
                        <Route path={"summary-inscription/:trip_id/:trip_id_version/:trip_token"} component={SummaryInscription} show_map_calendar={false} />
                        <Route path={"summary-documents/:trip_id/:trip_id_version/:trip_token"} component={SummaryDocuments} show_map_calendar={false} />
                        <Route path={"summary-contact/:trip_id/:trip_id_version/:trip_token"} component={SummaryContact} show_map_calendar={false} />
                        <Route path={"summary-quotation/payment/success/:trip_id/:trip_id_version/:trip_token"} component={SummaryPaymentSuccess} show_map_calendar={false} />
                        <Route path={"summary-quotation/payment/error/:trip_id/:trip_id_version/:trip_token"} component={SummaryPaymentError} show_map_calendar={false} />
                        <Route path={"provider-website/:trip_id/:trip_id_version/:trip_token/:product_type/:product_id"} component={ProviderWebsite} show_map_calendar={false} />
                        <Route path={"provider-quotation/provider/:providerId/trip/:tripId/version/:tripVersion/stack/:stackNumber/token/:tripToken"} component={ProviderQuotation} show_map_calendar={false} />
                        <Route path={"provider-booking/provider/:providerId/trip/:tripId/version/:tripVersion/token/:tripToken"} component={ProviderBooking} show_map_calendar={false} />
                        <Route path={"provider-cancel/:trip_id/:trip_id_version/:trip_token/:product_type/:product_id"} component={ProviderCancel} show_map_calendar={false} />
                        <Route path={"apps"} component={Base} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }}>
                            <IndexRedirect to={"itinerary"} />
                            {
                                //<Route path={ "steps" } component={ AppsSteps } show_map_calendar={ true } type={ 0 }/>
                            }
                            <Route path={"itinerary"} component={Itinerary} show_map_calendar={true} type={0} timeline={false} />
                            <Route path={"packages"} component={Itinerary} show_map_calendar={true} type={0} timeline={false} />
                            <Route path={"cruises"} component={Itinerary} show_map_calendar={true} type={0} timeline={false} />
                            <Route path={"flight-groups"} component={withCompatTheme(FlightGroups)} show_map_calendar={false} timeline={false} />
                            <Route
                                path={"flight-search/:use_cache"}
                                component={withCompatTheme((props) => (
                                    <FlightSearch
                                        {...props}
                                        isCms
                                    />
                                ))}
                                show_map_calendar={false}
                                onEnter={(router, replace, callback) => {
                                    checkFlight(router, replace, callback, store);
                                }}
                                timeline={false}
                            />
                            <Route path={"selected-flight"} component={withCompatTheme(SelectedFlight)} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                checkFlight(router, replace, callback, store);
                            }} timeline={false} />
                            <Route path={"accommodation"} component={withCompatTheme(AccommodationList)} show_map_calendar={true} type={1} timeline={true} />
                            <Route path={"accommodation-card/:id"} component={withCompatTheme(MaterialAccommodationCard)} type={1} show_map_calendar={false} timeline={false} />
                            <Route path={"poi"} component={withCompatTheme(PoiList)} show_map_calendar={true} type={2} timeline={true} />
                            <Route path={"poi-card/:id"} component={withCompatTheme(PoiCard)} show_map_calendar={false} timeline={false} />
                            <Route
                                path={"cart"}
                                component={Cart}
                                how_map_calendar={false}
                                timeline={false}
                                onLeave={() => {
                                    store.dispatch(resetCircuit());
                                    store.dispatch(setSeeProductsMargins(false));
                                }}
                            />
                            <Route path={"insurances"} component={Insurances} show_map_calendar={false} timeline={false} />
                            <Route path={"travelers"} component={Travelers} show_map_calendar={false} timeline={false} />
                            <Route path={"cart-quotation"} component={CartQuotation} show_map_calendar={false} timeline={false} />
                            <Route path={"booking"} component={Booking} show_map_calendar={false} timeline={false} />
                            <Route path={"cars-search"} component={CarsSearch} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                checkDepartureReturn(router, replace, callback, store);
                            }} timeline={false} />
                            <Route path={"material-cars-search"} component={withCompatTheme(MaterialCarsSearch)} timeline={false} show_map_calendar={false} name={""} />
                            <Route path={"cars-search-one-step"} component={CarsSearchOneStep} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                checkDepartureReturn(router, replace, callback, store);
                            }} timeline={false} />
                            <Route path={"cars-step"} component={CarsStep} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                checkDepartureReturn(router, replace, callback, store);
                            }} timeline={false} />
                            <Route path={"cars-results/:id_step"} component={CarsResults} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                checkDepartureReturn(router, replace, callback, store);
                            }} timeline={false} />
                            <Route path={"cars-add-to-cart/:id_step"} component={CarsAddToCart} show_map_calendar={false} onEnter={(router, replace, callback) => {
                                checkDepartureReturn(router, replace, callback, store);
                            }} timeline={false} />
                            <Route path={"transfers"} component={Transfers} show_map_calendar={false} timeline={false} />
                            <Route path={"step-transfers"} component={StepTransfers} show_map_calendar={false} timeline={false} />
                            <Route path={"city-transfers"} component={CityTransfers} show_map_calendar={false} timeline={false} />
                            <Route
                                path="circuit-steps"
                                component={CircuitSteps}
                                show_map_calendar={false}
                                onEnter={(router, replace) => {
                                    if (
                                        !router.location.query.circuit ||
                                        !router.location.query.version ||
                                        !router.location.query.variant
                                    ) {
                                        const circuit = store.getState().page.circuit.package.data;
                                        const url = localStorage.getItem('circuit-steps-last-circuit');
                                        if (!circuit && url) {
                                            const parsedUrl = new URL(url);
                                            replace({
                                                pathname: parsedUrl.pathname,
                                            });
                                        }
                                    }

                                    if (packageFunnel) {
                                        store.dispatch(
                                            setFunnelId(packageFunnel.id)
                                        );
                                    }

                                    SetCookie("trip_id", '', -365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
                                    SetCookie("trip_id_version", '', -365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
                                    SetCookie("trip_token", '', -365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
                                }}
                                onLeave={() => {
                                    if (tailorMadeFunnel) {
                                        store.dispatch(
                                            setFunnelId(tailorMadeFunnel.id)
                                        );
                                    }
                                    if (
                                        !window.location.pathname.includes('/login') &&
                                        !window.location.pathname.includes('/email-confirmation')
                                    ) {
                                        store.dispatch(resetCircuit());
                                    }
                                }}
                                timeline={false}
                                disabledLoader
                                disabledHeader
                            >
                                <Route
                                    path="parameters"
                                    component={CircuitStepsParameters}
                                />
                                <Route
                                    path={"flight-search/:use_cache"}
                                    component={withCompatTheme((props) => (
                                        <FlightSearch
                                            {...props}
                                            contentOnly
                                            isCircuitPrecart
                                        />
                                    ))}
                                    show_map_calendar={false}
                                    timeline={false}
                                />
                                <Route
                                    path={"selected-flight"}
                                    component={withCompatTheme((props) => (
                                        <SelectedFlight
                                            {...props}
                                            isCircuitPrecart
                                        />
                                    ))}
                                    show_map_calendar={false}
                                    onEnter={(router, replace, callback) => {
                                        checkFlight(router, replace, callback, store);
                                    }}
                                    timeline={false}
                                />
                                <Route
                                    path="accommodation"
                                    component={CircuitStepsAccommodation}
                                />
                                <Route path="options">
                                    <IndexRedirect to={"included"} />
                                    <Route
                                        path="included"
                                        component={CircuitStepsProducts}
                                    />
                                    <Route
                                        path="optional"
                                        component={CircuitStepsOptionalProducts}
                                    />
                                </Route>
                                <Route
                                    path="insurance"
                                    component={CircuitStepsInsurances}
                                />
                                <Route path="booking">
                                    <IndexRedirect to={"travelers"} />
                                    <Route path="travelers" component={CircuitBookingTravelers} />
                                    <Route path="cart-recap" component={CircuitStepsBookingCartRecap} />
                                </Route>
                            </Route>
                            <Route
                                path="circuit-b2b-steps"
                                component={CircuitB2bSteps}
                                show_map_calendar={false}
                                onEnter={(router, replace) => {
                                    if (
                                        !router.location.query.circuit ||
                                        !router.location.query.version ||
                                        !router.location.query.variant
                                    ) {
                                        const circuit = store.getState().page.circuit.package.data;
                                        const url = localStorage.getItem('circuit-steps-last-circuit');
                                        if (!circuit && url) {
                                            const parsedUrl = new URL(url);
                                            replace({
                                                pathname: parsedUrl.pathname,
                                            });
                                        }
                                    }

                                    if (packageFunnel) {
                                        store.dispatch(
                                            setFunnelId(packageFunnel.id)
                                        );
                                    }

                                    SetCookie("trip_id", '', -365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
                                    SetCookie("trip_id_version", '', -365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
                                    SetCookie("trip_token", '', -365, `/`, (window.location.host === "localhost:3000" ? null : window.location.host));
                                }}
                                onLeave={() => {
                                    if (tailorMadeFunnel) {
                                        store.dispatch(
                                            setFunnelId(tailorMadeFunnel.id)
                                        );
                                    }
                                    if (
                                        !window.location.pathname.includes('/login') &&
                                        !window.location.pathname.includes('/email-confirmation')
                                    ) {
                                        store.dispatch(resetCircuit());
                                    }
                                }}
                                timeline={false}
                                disabledLoader
                                disabledHeader
                            >
                                <Route
                                    path="parameters"
                                    component={CircuitB2bParameters}
                                />
                                <Route
                                    path={"flight-search/:use_cache"}
                                    component={withCompatTheme((props) => (
                                        <FlightSearch
                                            {...props}
                                            contentOnly
                                            isCircuitPrecart
                                        />
                                    ))}
                                    show_map_calendar={false}
                                    timeline={false}
                                />
                                <Route
                                    path="accommodation"
                                    component={CircuitB2bAccommodationBasedOnSource}
                                />
                                <Route
                                    path="pois"
                                    component={CircuitB2bPois}
                                />
                                <Route
                                    path="transfers"
                                    component={CircuitB2bTransfers}
                                />
                                <Route
                                    path="cars"
                                    component={CircuitB2bCars}
                                />
                                <Route path="options">
                                    <IndexRedirect to={"included"} />
                                    <Route
                                        path="included"
                                        component={CircuitB2bProducts}
                                    />
                                    <Route
                                        path="optional"
                                        component={CircuitB2bOptionalProducts}
                                    />
                                </Route>
                                <Route
                                    path="insurance"
                                    component={CircuitB2bInsurances}
                                />
                                <Route path="booking">
                                    <IndexRedirect to={"travelers"} />
                                    <Route path="travelers" component={CircuitBookingTravelers} />
                                    <Route path="cart-recap" component={CircuitB2bBookingCartRecap} />
                                </Route>
                            </Route>
                            <Route
                                path="circuit-cart-steps"
                                component={CircuitCartSteps}
                                show_map_calendar={false}
                                timeline={false}
                                onEnter={() => {
                                    if (packageFunnel) {
                                        store.dispatch(
                                            setFunnelId(packageFunnel.id)
                                        );
                                    }
                                }}
                                onLeave={() => {
                                    if (tailorMadeFunnel) {
                                        store.dispatch(
                                            setFunnelId(tailorMadeFunnel.id)
                                        );
                                    }
                                }}
                                disabledLoader
                                disabledHeader
                            >
                                <Route
                                    path="booking"
                                    component={CircuitCartStepsBooking}
                                >
                                    <IndexRedirect to={"cart-recap"} />
                                    <Route path="travelers" component={CircuitCartStepsTravelers} />
                                    <Route
                                        path="cart-recap"
                                        component={CircuitCartStepsBookingCartRecap}
                                        onLeave={() => {
                                            store.dispatch(setSeeProductsMargins(false));
                                        }}
                                    />
                                    <Route path={"documents"} component={TripListDocumentPage} />
                                </Route>
                                <Route path="accommodation" component={CircuitCartStepsAccommodation} />
                                <Route path="insurance" component={CircuitCartStepsInsurances} />
                                <Route path="flight" component={CircuitCartStepsFlights} />
                                <Route path="options">
                                    <IndexRedirect to={"included"} />
                                    <Route path="included" component={CircuitCartStepsProducts} />
                                    <Route path="optional" component={CircuitCartStepsOptionalProducts} />
                                </Route>
                            </Route>
                        </Route>
                        <Route
                            path="page-builder/:pageId"
                            component={PageBuilder}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                            }}
                        />
                        <Route
                            path="preview/:pageId"
                            component={PageBuilderPreview}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                            }}
                        />
                        <Route
                            path="template/container/preview/:templateId"
                            component={PageBuilderTemplatePreview}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                            }}
                        />
                        <Route
                            path="template/page/preview/:templateId"
                            component={PageBuilderTemplatePreview}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                            }}
                        />
                        <Route
                            path={"content/settings"}
                            component={CmsSettings}
                            onEnter={(router, replace, callback) => {
                                requireAuth(router, replace, callback);
                            }}
                        >
                            <IndexRedirect to={"themes"} />
                            <Route path="themes" component={ThemeSettingsList} />
                            <Route path="themes/colors/:themeId" component={ThemeSettingsColors} />
                            <Route path="themes/inputs/:themeId" component={ThemeSettingsInputs} />
                            <Route path="themes/typography/:themeId" component={ThemeSettingsTypography} />
                            <Route path="themes/buttons/:themeId" component={ThemeSettingsButtons} />
                            <Route path="themes/icons/:themeId" component={ThemeSettingsIcons} />
                            <Route path="pages" component={PagesList} />
                            <Route path="menus" component={MenusList} />
                            <Route path="menus/:id" component={MenuEdit} />
                        </Route>
                        <Route path={"menu"} component={BaseMenu} onEnter={(router, replace, callback) => {
                            requireAuth(router, replace, callback);
                        }} timeline={false}>
                            <IndexRedirect to={"profile"} />
                            <Route path={"profile"} component={Profile} />
                            <Route path={"agency-profile"} component={AgencyProfile} />
                            <Route path={"salespersons"} component={SalesPersons} />
                            <Route path={"trip-list"} component={TripList} disabledHeader timeline={false} />
                            <Route path={"trip-info/:trip_id"} component={TripListInfo} />
                            <Route path={"agency-margin"} component={AgencyMargin} />
                            <Route path={"agency-user-management"} component={AgencyUserManagement} />
                            <Route path={"agency-customer-management"} component={AgencyCustomerManagement} />
                            <Route path={"help"} component={Help} />
                            <Route path={"settings"} component={Settings} />
                        </Route>
                    </Route>
                    <Route
                        path="*"
                        component={PageBuilderViewPage}
                    />
                </Route>
            </Router>
        </RoutesWrapper>,
        document.getElementById("app")
    );
}
